import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  CategoryOutlined as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import ArticuloService from '../../services/articulos';
import CategoriaService from '../../services/categorias';

// Warm pastel palette — matches app header
const ACCENT       = '#c0714a';
const ACCENT_LIGHT = '#fde8d4';
const ACCENT_BG    = '#fff8f2';

const TYPE_COLOR = {
  producto: { bg: '#fdecea', color: '#b94040' },
  servicio: { bg: '#fef6e4', color: '#9a6010' },
  digital:  { bg: '#e8f6ee', color: '#2e7d55' },
};
const typeChipSx = (type) => {
  const t = TYPE_COLOR[type?.toLowerCase()] || { bg: ACCENT_LIGHT, color: ACCENT };
  return { backgroundColor: t.bg, color: t.color, fontWeight: 700, fontSize: '0.68rem', height: 20 };
};

// Extracts the current category id from the raw article object
const getCurrentCategoryId = (article) =>
  article?.article?.category_id ||
  article?.article?.category?._id ||
  article?.category_id ||
  null;

/**
 * Dialog para recategorizar un artículo.
 *
 * Props:
 *   open         boolean
 *   onClose      () => void
 *   article      objeto con al menos { article: { name }, article_id, store_id }
 *   storeId      string — ID de la tienda
 *   onSuccess    (articleId, categoryId) => void
 */
const RecategorizarDialog = ({ open, onClose, article, storeId, onSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load categories when dialog opens
  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setSaved(false);
    setSearch('');
    setLoadingCats(true);
    CategoriaService.getAll()
      .then((data) => setCategories(data || []))
      .catch(() => setCategories([]))
      .finally(() => setLoadingCats(false));
  }, [open]);

  const currentCategoryId = getCurrentCategoryId(article);
  const currentCategory = categories.find((c) => c._id === currentCategoryId) || null;

  const filtered = categories.filter(
    (c) =>
      !search ||
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.type?.toLowerCase().includes(search.toLowerCase())
  );

  const articleId = article?.article_id || article?._id;
  const articleName = article?.article?.name || article?.name || '—';
  const effectiveStoreId = storeId || article?.store_id;

  const handleConfirm = async () => {
    if (!selected || !articleId || !effectiveStoreId) return;
    setSaving(true);
    try {
      await ArticuloService.recategorize(effectiveStoreId, articleId, selected._id);
      setSaved(true);
      onSuccess && onSuccess(articleId, selected._id, selected);
      setTimeout(() => {
        setSaved(false);
        onClose();
      }, 1200);
    } catch {
      // error handled silently — could add a snackbar here
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: ACCENT_BG,
          border: `1px solid ${ACCENT_LIGHT}`,
        },
      }}
    >
      {/* ── Header ─────────────────────────────── */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 34, height: 34, borderRadius: '50%',
                backgroundColor: ACCENT_LIGHT,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <SwapIcon sx={{ fontSize: 18, color: ACCENT }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: ACCENT, lineHeight: 1.2 }}>
                Recategorizar
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: '0.72rem',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {articleName}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: ACCENT_LIGHT }} />

      <DialogContent sx={{ pt: 2, pb: 2, px: 2.5 }}>

        {/* ── Current → Selected banner ────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            p: 1.2,
            borderRadius: 2,
            backgroundColor: 'white',
            border: `1px solid ${ACCENT_LIGHT}`,
          }}
        >
          {/* Current category */}
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
              Actual
            </Typography>
            {loadingCats ? (
              <CircularProgress size={14} sx={{ color: ACCENT }} />
            ) : currentCategory ? (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: ACCENT, lineHeight: 1.2 }}>
                  {currentCategory.name}
                </Typography>
                {currentCategory.type && (
                  <Chip label={currentCategory.type} size="small" sx={{ ...typeChipSx(currentCategory.type), mt: 0.4 }} />
                )}
              </>
            ) : (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
                Sin categoría
              </Typography>
            )}
          </Box>

          {/* Arrow */}
          <ArrowIcon sx={{ color: ACCENT_LIGHT, fontSize: 20, flexShrink: 0 }} />

          {/* Target category */}
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
              Nueva
            </Typography>
            {selected ? (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#2e7d32', lineHeight: 1.2 }}>
                  {selected.name}
                </Typography>
                {selected.type && (
                  <Chip label={selected.type} size="small" sx={{ ...typeChipSx(selected.type), mt: 0.4 }} />
                )}
              </>
            ) : (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
                Seleccioná abajo
              </Typography>
            )}
          </Box>
        </Box>

        {/* Search */}
        <TextField
          placeholder="Buscar categoría..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Category list */}
        <Box
          sx={{
            maxHeight: 260,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.8,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { borderRadius: 2, backgroundColor: ACCENT_LIGHT },
          }}
        >
          {loadingCats ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} sx={{ color: ACCENT }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 3, display: 'block' }}>
              {search ? 'Sin resultados' : 'No hay categorías disponibles'}
            </Typography>
          ) : (
            filtered.map((cat) => {
              const isSelected = selected?._id === cat._id;
              const isCurrent = cat._id === currentCategoryId;
              return (
                <Box
                  key={cat._id}
                  onClick={() => !isCurrent && setSelected(isSelected ? null : cat)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    cursor: isCurrent ? 'not-allowed' : 'pointer',
                    border: '1px solid',
                    borderColor: isCurrent
                      ? '#e8d5c4'
                      : isSelected ? ACCENT : `${ACCENT_LIGHT}cc`,
                    backgroundColor: isCurrent
                      ? '#fff3e8'
                      : isSelected ? ACCENT_LIGHT : 'white',
                    opacity: isCurrent ? 0.65 : 1,
                    transition: 'all 0.15s',
                    ...(!isCurrent && {
                      '&:hover': {
                        borderColor: ACCENT,
                        backgroundColor: isSelected ? ACCENT_LIGHT : '#fff3ec',
                      },
                    }),
                  }}
                >
                  <Box
                    sx={{
                      width: 30, height: 30, borderRadius: '50%',
                      backgroundColor: isCurrent
                        ? '#e8d5c4'
                        : isSelected ? ACCENT : '#f0e0d0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'background 0.15s',
                    }}
                  >
                    {isSelected
                      ? <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
                      : <CategoryIcon sx={{ fontSize: 15, color: isCurrent ? '#c09070' : ACCENT }} />
                    }
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Typography
                        sx={{
                          fontWeight: isSelected ? 700 : 500,
                          fontSize: '0.85rem',
                          color: isCurrent ? '#b08060' : isSelected ? ACCENT : 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {cat.name}
                      </Typography>
                      {isCurrent && (
                        <Chip
                          label="actual"
                          size="small"
                          sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, backgroundColor: '#fde8d4', color: '#c09070' }}
                        />
                      )}
                    </Box>
                    {cat.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.68rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                      >
                        {cat.description}
                      </Typography>
                    )}
                  </Box>
                  {cat.type && (
                    <Chip label={cat.type} size="small" sx={typeChipSx(cat.type)} />
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
          <Button
            size="small"
            variant="text"
            onClick={onClose}
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirm}
            disabled={!selected || saving || saved}
            startIcon={
              saved
                ? <CheckCircleIcon sx={{ fontSize: 16 }} />
                : saving
                ? <CircularProgress size={14} color="inherit" />
                : <SwapIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              backgroundColor: saved ? '#2e7d32' : ACCENT,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 130,
              transition: 'background 0.3s',
              '&:hover': { backgroundColor: saved ? '#1b5e20' : '#a8603d' },
              '&.Mui-disabled': { backgroundColor: '#e0b8a0', color: 'white' },
            }}
          >
            {saved ? '¡Listo!' : saving ? 'Guardando...' : `Cambiar a "${selected?.name || '...'}" `}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RecategorizarDialog;
